// Dashboard de KPIs de validación de servicios TP (GRS, U7).
//
// Muestra totales de validaciones automáticas vs manuales y una lista por
// usuario (Usuario, Servicios validados, Mes). El rango de fecha reutiliza el
// DateRangeFilter compartido (Hoy, Ayer, 7/15/30 días, rango personalizado).
//
// Datos: getServiceValidationDashboard(token, { date_from, date_to }).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useAuth0 } from "@auth0/auth0-react"
import ReactApexChart from "react-apexcharts"
import {
  Container, Row, Col, Card, CardBody, CardTitle, Table, Badge, Spinner, Alert, Button, Input,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap"
import classnames from "classnames"
import { withTranslation } from "react-i18next"

import { Permissions } from "../../constants/enums"
import {
  getServiceValidationDashboard,
  getServiceValidationDashboardExport,
  getServiceValidationDashboardUsers,
  getServiceValidationDashboardByHour,
  getServiceValidationDashboardKpis,
} from "../../helpers/api_rombo_helper"
import { dateRangeValue, DATE_RANGE_DEFAULT } from "./filterDefaults"
import { downloadBlob, timestampedName } from "./downloadFile"
import DateRangeFilter from "./DateRangeFilter"
import ServiceValidationTour from "./ServiceValidationTour"
import {
  formatNumber,
  formatCurrency,
  formatPercentPoints,
  formatDuration as fmtKpiDuration,
  formatDelta,
  formatDeltaPct,
  deltaDirection,
} from "./kpiFormat"

// Orden y configuración de presentación de los KPIs del set `kpis` (contrato
// backend). `fmt` es el formateador de valor por métrica; `pct` indica una tasa
// en puntos % (delta en puntos %); `currency`/`duration` eligen el formateador.
const KPI_ROWS = [
  { key: "validated", labelKey: "Validated", fmt: "number" },
  { key: "validated_auto", labelKey: "ValidatedAuto", fmt: "number" },
  { key: "validated_manual", labelKey: "ValidatedManual", fmt: "number" },
  { key: "validated_auto_pct", labelKey: "ValidatedAutoPct", fmt: "percent" },
  { key: "on_hold", labelKey: "OnHold", fmt: "number" },
  { key: "pending", labelKey: "Pending", fmt: "number" },
  { key: "amount_total", labelKey: "AmountTotal", fmt: "currency" },
  { key: "avg_validation_seconds", labelKey: "AvgValidationTime", fmt: "duration" },
  { key: "audit_actions", labelKey: "AuditActions", fmt: "number" },
]

// Verbos del desglose de auditoría (audit_breakdown), en el orden del contrato.
const AUDIT_ROWS = [
  { key: "validate", labelKey: "AuditValidate" },
  { key: "unvalidate", labelKey: "AuditUnvalidate" },
  { key: "edit", labelKey: "AuditEdit" },
  { key: "cancel", labelKey: "AuditCancel" },
  { key: "hold", labelKey: "AuditHold" },
  { key: "release", labelKey: "AuditRelease" },
  { key: "pod_delete", labelKey: "AuditPodDelete" },
  { key: "other", labelKey: "AuditOther" },
]

// Columnas de KpiMetric por usuario (by_user), en orden de presentación.
const BY_USER_METRICS = [
  { key: "validated", labelKey: "Validated", fmt: "number" },
  { key: "validated_auto", labelKey: "ValidatedAuto", fmt: "number" },
  { key: "validated_manual", labelKey: "ValidatedManual", fmt: "number" },
  { key: "on_hold", labelKey: "OnHold", fmt: "number" },
  { key: "amount_total", labelKey: "AmountTotal", fmt: "currency" },
  { key: "avg_validation_seconds", labelKey: "AvgValidationTime", fmt: "duration" },
  { key: "audit_actions", labelKey: "AuditActions", fmt: "number" },
]

// Resuelve el formateador de VALOR para un tipo de métrica KPI.
const valueFormatter = (fmt) => {
  if (fmt === "currency") return formatCurrency
  if (fmt === "duration") return fmtKpiDuration
  if (fmt === "percent") return formatPercentPoints
  return formatNumber
}

// Clase de color Bootstrap según la dirección del delta (sube=success,
// baja=danger, plano=muted). Puramente estético.
const deltaColorClass = (metric) => {
  const dir = deltaDirection(metric)
  if (dir === "up") return "text-success"
  if (dir === "down") return "text-danger"
  return "text-muted"
}

// Renderiza la celda de comparativo de un KpiMetric: delta absoluto + delta %
// ('—'/'nuevo' cuando delta_pct es null). `fmt` es el tipo de la métrica.
const ComparisonCell = ({ metric, fmt, t }) => {
  const valueFmt = valueFormatter(fmt)
  const pct = formatDeltaPct(metric)
  return (
    <span className={deltaColorClass(metric)}>
      {formatDelta(metric, valueFmt)}
      {" · "}
      {pct == null ? (
        <span title={t("NewLabel")}>— {t("NewLabel")}</span>
      ) : pct}
    </span>
  )
}

// Tarjeta de KPI (número grande + etiqueta + icono/color semántico).
const KpiCard = ({ icon, color, label, value, hint }) => (
  <Card className="h-100">
    <CardBody className="d-flex align-items-center gap-3">
      <div className={`avatar-sm d-flex align-items-center justify-content-center rounded bg-${color}-subtle`}>
        <i className={`bx ${icon} fs-3 text-${color}`} />
      </div>
      <div>
        <div className="text-muted small">{label}</div>
        <h3 className="mb-0">{value}</h3>
        {hint ? <div className="text-muted small">{hint}</div> : null}
      </div>
    </CardBody>
  </Card>
)

// `view` selecciona qué sub-vista del dashboard se muestra:
//   - "general": dashboard legacy (tarjetas, gráficas, por hora, por usuario).
//   - "kpis":    KPIs comparativos (período actual vs anterior) + por usuario.
// Cada vista solo monta su JSX y solo dispara SUS cargas de datos, de modo que
// entrar a una ruta no pide datos de la otra. Las rutas
// /service-validation/dashboard/{general,kpis} montan este componente con el
// `view` correspondiente.
const ServiceValidationDashboard = ({ t, view = "general" }) => {
  const { getAccessTokenSilently } = useAuth0()
  const showGeneral = view === "general"
  const showKpis = view === "kpis"

  const [infoUser, setInfoUser] = useState({ permissions: [] })
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  // Rango de fecha (controlado), por defecto "Hoy".
  const [range, setRange] = useState(() => {
    const { date_from, date_to } = dateRangeValue(DATE_RANGE_DEFAULT)
    return { range: DATE_RANGE_DEFAULT, date_from, date_to }
  })
  // Granularidad del reporte por usuario: día / semana / mes.
  const [groupBy, setGroupBy] = useState("month")
  // Filtro por validador (sub). "" = todos. Las opciones se cargan una vez.
  const [userFilter, setUserFilter] = useState("")
  const [users, setUsers] = useState([])
  // Drill-down horario: período seleccionado al hacer click en una columna del
  // gráfico "Validados por período" y su serie de horas (0-23) para ese período.
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const [periodByHour, setPeriodByHour] = useState(null)
  // Tutorial del dashboard (disparo manual).
  const [tourOpen, setTourOpen] = useState(false)
  // Estado de las exportaciones (Excel backend / PDF navegador).
  const [exportingXlsx, setExportingXlsx] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  // Contenedor capturado para el PDF (indicadores + gráficas).
  const captureRef = useRef(null)

  // --- Bloque de KPIs comparativos (período actual vs anterior). Estado
  // dedicado para no interferir con el dashboard legacy. Dos modos (contrato):
  // granularity (sin fechas) vs rango personalizado (date_from/date_to).
  const [kpisData, setKpisData] = useState(null)
  const [kpisLoading, setKpisLoading] = useState(false)
  const [kpisError, setKpisError] = useState(null)
  // Granularidad del bloque KPIs: día / semana / mes (default "day", del contrato).
  const [kpiGranularity, setKpiGranularity] = useState("day")
  // Tab activa del bloque de KPIs comparativos. Orden: Por usuario (default) /
  // KPIs / Auditoría.
  const [kpiTab, setKpiTab] = useState("byuser")
  // Rango personalizado del bloque KPIs. range === "custom" => se envían fechas
  // (custom_range=true); cualquier preset con fechas también activa el modo rango.
  const [kpiRange, setKpiRange] = useState(() => {
    const { date_from, date_to } = dateRangeValue(DATE_RANGE_DEFAULT)
    return { range: DATE_RANGE_DEFAULT, date_from, date_to }
  })

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ data: Login.data, loading: Login.loading }),
  )
  const { data: loginData, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (loginData?.data) setInfoUser(loginData.data)
  }, [loginData, loading])

  const canRead = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_DASHBOARD_READ)

  const authHeader = useCallback(async () => {
    const accessToken = await getAccessTokenSilently()
    return { Authorization: `Bearer ${accessToken}` }
  }, [getAccessTokenSilently])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await authHeader()
      const res = await getServiceValidationDashboard(token, {
        date_from: range.date_from,
        date_to: range.date_to,
        group_by: groupBy,
        user: userFilter || undefined,
      })
      setData(res || null)
    } catch (_e) {
      setError(t("LoadError"))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [authHeader, range.date_from, range.date_to, groupBy, userFilter, t])

  // FIX TAREA 1B (defensa en profundidad): condicionar la carga a `canRead`. Un
  // usuario sin permiso que llegue por URL NO debe disparar la petición (antes
  // `load()` corría siempre al montar, independiente de canRead). El backend
  // mantiene el gate OR permiso/rol (opción B); esto es la defensa del cliente.
  useEffect(() => { if (canRead && showGeneral) load() }, [load, canRead, showGeneral])

  // Carga de los KPIs comparativos. Dos modos del contrato:
  // - Modo granularidad (preset "today"): se envía sólo `granularity` (day/week/
  //   month) y el backend resuelve el período de hoy vs el inmediato anterior
  //   (custom_range=false). Respeta el selector día/semana/mes.
  // - Modo rango (cualquier OTRO preset: yesterday/last7/last15/last30/custom):
  //   se envían date_from/date_to TAL CUAL (sin conversión de zona) y el backend
  //   pone custom_range=true, comparando contra el bloque previo de igual
  //   duración. Así los presets de rango dejan de ser UI muerta.
  const loadKpis = useCallback(async () => {
    setKpisLoading(true)
    setKpisError(null)
    try {
      const token = await authHeader()
      // "today" => modo granularidad; todo preset con rango resuelto => modo rango.
      const useRange = kpiRange.range !== "today" && Boolean(kpiRange.date_from && kpiRange.date_to)
      const res = await getServiceValidationDashboardKpis(token, {
        granularity: kpiGranularity,
        date_from: useRange ? kpiRange.date_from : undefined,
        date_to: useRange ? kpiRange.date_to : undefined,
        user: userFilter || undefined,
      })
      setKpisData(res || null)
    } catch (_e) {
      setKpisError(t("LoadError"))
      setKpisData(null)
    } finally {
      setKpisLoading(false)
    }
  }, [authHeader, kpiGranularity, kpiRange.range, kpiRange.date_from, kpiRange.date_to, userFilter, t])

  // Gateado por canRead (ver fix 1B) y por la vista activa: solo la sub-vista
  // "kpis" dispara esta carga.
  useEffect(() => { if (canRead && showKpis) loadKpis() }, [loadKpis, canRead, showKpis])

  // Carga (una vez) la lista de usuarios validadores únicos para el filtro. El
  // backend la sirve con caché agresiva, así que es barato y estable.
  useEffect(() => {
    let cancelled = false
    const loadUsers = async () => {
      try {
        const token = await authHeader()
        const res = await getServiceValidationDashboardUsers(token)
        if (!cancelled) setUsers(res?.users || [])
      } catch (_e) {
        if (!cancelled) setUsers([])
      }
    }
    if (canRead && showGeneral) loadUsers()
    return () => { cancelled = true }
  }, [authHeader, canRead, showGeneral])

  // Al cambiar los filtros base (fecha/usuario/granularidad) se invalida la
  // selección de período (el drill-down horario deja de ser coherente).
  useEffect(() => {
    setSelectedPeriod(null)
    setPeriodByHour(null)
  }, [range.date_from, range.date_to, groupBy, userFilter])

  // Drill-down: pide al backend las validaciones por hora del período clicado.
  const loadPeriodByHour = useCallback(async (period) => {
    try {
      const token = await authHeader()
      const res = await getServiceValidationDashboardByHour(token, {
        date_from: range.date_from,
        date_to: range.date_to,
        group_by: groupBy,
        period,
        user: userFilter || undefined,
      })
      setPeriodByHour(res?.by_hour || [])
    } catch (_e) {
      setPeriodByHour([])
    }
  }, [authHeader, range.date_from, range.date_to, groupBy, userFilter])

  // El backend devuelve los totales PLANOS (auto/manual/total/edited) a nivel
  // raíz de la respuesta.
  const totals = {
    auto: Number(data?.auto) || 0,
    manual: Number(data?.manual) || 0,
    total: Number(data?.total) || 0,
    edited: Number(data?.edited) || 0,
  }
  const byUser = data?.by_user || []
  const byHour = data?.by_hour || []
  // Mapa sub -> nombre a partir de la lista de validadores (cargada para el
  // filtro). Permite mostrar el nombre del usuario en la tabla "por usuario"
  // en lugar del sub opaco. Fallback al sub si no hay nombre.
  const subToName = useMemo(
    () => new Map((users || []).map((u) => [u.sub, u.name])),
    [users],
  )
  // ¿Hay información que exportar? Hay KPIs con total > 0 o filas por usuario.
  const hasData = totals.total > 0 || totals.auto > 0 || totals.manual > 0 || byUser.length > 0
  // % automático sobre el total (para el hint de la tarjeta).
  const autoPct = useMemo(() => {
    const tot = totals.total
    if (!tot) return null
    return Math.round(totals.auto * 100 / tot)
  }, [totals.total, totals.auto])

  // Exportar el REPORTE a Excel: lo genera el BACKEND (pandas/openpyxl) con los
  // mismos filtros (rango + granularidad) y aquí solo se dispara la descarga.
  const onExportXlsx = useCallback(async () => {
    setExportingXlsx(true)
    try {
      const token = await authHeader()
      const blob = await getServiceValidationDashboardExport(token, {
        date_from: range.date_from,
        date_to: range.date_to,
        group_by: groupBy,
      })
      downloadBlob(blob, timestampedName("reporte-validacion"))
    } catch (_e) {
      setError(t("ExportError") || "No se pudo exportar el reporte.")
    } finally {
      setExportingXlsx(false)
    }
  }, [authHeader, range.date_from, range.date_to, groupBy, t])

  // Exportar a PDF los indicadores + gráficas DESDE EL NAVEGADOR: se captura el
  // contenedor (captureRef) con html2canvas y se arma un PDF con jsPDF, paginando
  // si el alto excede una página A4. Los libs se cargan dinámicamente (code-split)
  // para no inflar el bundle principal; solo pesan al hacer clic.
  const onExportPdf = useCallback(async () => {
    const node = captureRef.current
    if (!node) return
    setExportingPdf(true)
    try {
      const [{ default: html2canvas }, jspdfMod] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ])
      const JsPDF = jspdfMod.jsPDF || jspdfMod.default
      const canvas = await html2canvas(node, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false,
      })
      const imgData = canvas.toDataURL("image/png")
      const pdf = new JsPDF("p", "mm", "a4")
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      // Escala la imagen al ancho de página y pagina verticalmente.
      const imgW = pageW
      const imgH = (canvas.height * imgW) / canvas.width
      let heightLeft = imgH
      let position = 0
      pdf.addImage(imgData, "PNG", 0, position, imgW, imgH)
      heightLeft -= pageH
      while (heightLeft > 0) {
        position -= pageH
        pdf.addPage()
        pdf.addImage(imgData, "PNG", 0, position, imgW, imgH)
        heightLeft -= pageH
      }
      pdf.save(timestampedName("dashboard-validacion", "pdf"))
    } catch (_e) {
      setError(t("ExportError") || "No se pudo exportar el PDF.")
    } finally {
      setExportingPdf(false)
    }
  }, [t])

  // --- Gráfica 1: dona de distribución de validaciones (auto/manual/editadas).
  // "edited" es un subconjunto de validadas: se grafica aparte como referencia.
  const donutLabels = useMemo(
    () => [
      t("AutoValidations") || "Automáticas",
      t("ManualValidations") || "Manuales",
      t("EditedServices") || "Editadas",
    ],
    [t],
  )
  const donutSeries = useMemo(
    () => [totals.auto, totals.manual, totals.edited],
    [totals.auto, totals.manual, totals.edited],
  )
  const donutOptions = useMemo(
    () => ({
      labels: donutLabels,
      // success / warning / info (coherente con las tarjetas KPI).
      colors: ["#34c38f", "#f1b44c", "#50a5f1"],
      legend: { position: "bottom" },
      dataLabels: { enabled: true },
      stroke: { width: 1 },
      noData: { text: t("NoResults") || "Sin datos" },
    }),
    [donutLabels, t],
  )

  // --- Gráfica 2: barras de servicios validados por período (sumando usuarios).
  // Agrupa by_user por período y suma validated_count.
  const byPeriod = useMemo(() => {
    const acc = new Map()
    for (const row of byUser) {
      const key = row.period || row.month || "—"
      acc.set(key, (acc.get(key) || 0) + (Number(row.validated_count) || 0))
    }
    // Orden ascendente por etiqueta de período (YYYY-MM / YYYY-MM-DD / IYYY-Www).
    return Array.from(acc.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0])))
  }, [byUser])

  const barCategories = useMemo(() => byPeriod.map(([k]) => k), [byPeriod])
  const barSeries = useMemo(
    () => [{ name: t("ValidatedServices") || "Validados", data: byPeriod.map(([, v]) => v) }],
    [byPeriod, t],
  )
  const barOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        // Columnas interactivas: al hacer click en una barra se selecciona el
        // período y se pide su desglose horario (drill-down).
        events: {
          dataPointSelection: (_e, _ctx, cfg) => {
            const period = barCategories[cfg.dataPointIndex]
            if (!period) return
            setSelectedPeriod(period)
            loadPeriodByHour(period)
          },
        },
      },
      colors: ["#556ee6"],
      plotOptions: {
        bar: { borderRadius: 4, columnWidth: "45%" },
      },
      states: { active: { allowMultipleDataPointsSelection: false } },
      dataLabels: { enabled: false },
      xaxis: { categories: barCategories },
      tooltip: { intersect: false },
      noData: { text: t("NoResults") || "Sin datos" },
    }),
    [barCategories, loadPeriodByHour],
  )

  // Formatea segundos a una duración legible (h/m/s). "—" si no hay dato.
  const fmtDuration = (secs) => {
    if (secs == null || !Number.isFinite(Number(secs))) return "—"
    const s = Math.round(Number(secs))
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${sec}s` : `${sec}s`
  }

  // --- Gráfica 3: servicios validados por hora del día (0-23). Si hay un
  // período seleccionado (click en el gráfico de período) se grafican las horas
  // de ESE período (drill-down); si no, el agregado del rango completo.
  const activeHourRows = (selectedPeriod && periodByHour) ? periodByHour : byHour
  const hourCategories = useMemo(
    () => activeHourRows.map((r) => `${String(r.hour).padStart(2, "0")}:00`),
    [activeHourRows],
  )
  const hourSeries = useMemo(
    () => [{ name: t("ValidatedServices") || "Validados", data: activeHourRows.map((r) => Number(r.validated_count) || 0) }],
    [activeHourRows, t],
  )
  const hourOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      colors: ["#34c38f"],
      plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: hourCategories, labels: { rotate: -45, style: { fontSize: "10px" } } },
      noData: { text: t("NoResults") || "Sin datos" },
    }),
    [hourCategories, t],
  )

  if (!canRead) {
    return (
      <div className="page-content">
        <Container fluid>
          <span className="error-message-requests">
            <i className="fa fa-exclamation-circle" /> {t("NoPermissionsViewServices")}
          </span>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col xs={12} className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h4 className="mb-0">
              <i className="bx bx-bar-chart-alt-2 me-2" />
              {t("ServiceValidation")} · {t("Dashboard")} · {showKpis ? t("KpisComparative") : t("DashboardGeneral")}
            </h4>
            {showGeneral && (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div style={{ minWidth: 260 }} data-tour="svdash-date">
                <DateRangeFilter t={t} value={range} onChange={setRange} />
              </div>
              {/* Filtro por validador. "" = todos. Opciones cacheadas en backend. */}
              <div style={{ minWidth: 200 }} data-tour="svdash-user">
                <Input type="select" bsSize="sm" value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  aria-label={t("User") || "Usuario"}>
                  <option value="">{t("AllUsers") || "Todos los usuarios"}</option>
                  {users.map((u) => (
                    <option key={u.sub} value={u.sub}>{u.name || u.sub}</option>
                  ))}
                </Input>
              </div>
              {/* Tutorial del dashboard (disparo manual). */}
              <Button color="light" onClick={() => setTourOpen(true)}>
                <i className="bx bx-help-circle me-1" />
                {t("ViewTutorial") || "Ver tutorial"}
              </Button>
              <div data-tour="svdash-export" className="d-flex align-items-center gap-2">
                {/* Exportar REPORTE a Excel (generado en backend). */}
                <Button color="success" outline disabled={exportingXlsx || isLoading || !hasData}
                  onClick={onExportXlsx}>
                  {exportingXlsx
                    ? <Spinner size="sm" className="me-1" />
                    : <i className="bx bx-file me-1" />}
                  {t("ExportExcel") || "Exportar Excel"}
                </Button>
                {/* Exportar indicadores + gráficas a PDF (desde el navegador). */}
                <Button color="danger" outline disabled={exportingPdf || isLoading || !hasData}
                  onClick={onExportPdf}>
                  {exportingPdf
                    ? <Spinner size="sm" className="me-1" />
                    : <i className="bx bxs-file-pdf me-1" />}
                  {t("ExportPdf") || "Exportar PDF"}
                </Button>
              </div>
            </div>
            )}
          </Col>
        </Row>

        {showGeneral && error && <Alert color="danger">{error}</Alert>}

        {showGeneral && (isLoading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <div ref={captureRef}>
            {/* Tarjetas de KPI: automáticas / manuales / editadas / total */}
            <Row className="g-3 mb-3" data-tour="svdash-kpis">
              <Col md={3}>
                <KpiCard
                  icon="bx-check-shield" color="success"
                  label={t("AutoValidations")} value={totals.auto}
                  hint={autoPct != null ? `${autoPct}% ${t("OfTotal")}` : null} />
              </Col>
              <Col md={3}>
                <KpiCard
                  icon="bx-user-check" color="warning"
                  label={t("ManualValidations")} value={totals.manual} />
              </Col>
              <Col md={3}>
                <KpiCard
                  icon="bx-edit" color="info"
                  label={t("EditedServices") || "Servicios editados"} value={totals.edited} />
              </Col>
              <Col md={3}>
                <KpiCard
                  icon="bx-list-check" color="primary"
                  label={t("TotalValidations")} value={totals.total} />
              </Col>
            </Row>

            {/* Gráficas: distribución (dona) + validados por período (barras) */}
            <Row className="g-3 mb-3">
              <Col lg={5}>
                <Card className="h-100">
                  <CardBody>
                    <CardTitle tag="h5" className="mb-3">
                      <i className="bx bx-pie-chart-alt-2 me-1" />
                      {t("ValidationDistribution") || "Distribución"}
                    </CardTitle>
                    <ReactApexChart
                      options={donutOptions} series={donutSeries}
                      type="donut" height={300} />
                  </CardBody>
                </Card>
              </Col>
              <Col lg={7}>
                <Card className="h-100">
                  <CardBody data-tour="svdash-period">
                    <CardTitle tag="h5" className="mb-1">
                      <i className="bx bx-bar-chart me-1" />
                      {t("ValidatedByPeriod") || "Periodo"}
                    </CardTitle>
                    <div className="text-muted small mb-2">
                      <i className="bx bx-pointer me-1" />
                      {t("ClickColumnForHourly") || "Haz clic en una columna para ver el desglose por hora"}
                    </div>
                    <ReactApexChart
                      options={barOptions} series={barSeries}
                      type="bar" height={300} />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Servicios validados por hora del día (del período seleccionado
                si hay drill-down activo, si no del rango completo). */}
            <Row className="g-3 mb-3">
              <Col xs={12}>
                <Card>
                  <CardBody data-tour="svdash-byhour">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <CardTitle tag="h5" className="mb-0">
                        <i className="bx bx-time-five me-1" />
                        {t("ValidatedByHour") || "Validados por hora"}
                        {selectedPeriod ? (
                          <Badge color="info" className="ms-2 align-middle">{selectedPeriod}</Badge>
                        ) : null}
                      </CardTitle>
                      {selectedPeriod ? (
                        <Button color="link" size="sm" className="p-0 text-decoration-none"
                          onClick={() => { setSelectedPeriod(null); setPeriodByHour(null) }}>
                          <i className="bx bx-x me-1" />
                          {t("ClearSelection") || "Quitar filtro de período"}
                        </Button>
                      ) : null}
                    </div>
                    <ReactApexChart
                      options={hourOptions} series={hourSeries}
                      type="bar" height={280} />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Lista por usuario */}
            <Card>
              <CardBody data-tour="svdash-byuser">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <CardTitle tag="h5" className="mb-0">
                    <i className="bx bx-group me-1" />{t("ValidationsByUser")}
                  </CardTitle>
                  {/* Granularidad del período: Día / Semana / Mes. */}
                  <div className="btn-group btn-group-sm" role="group" aria-label={t("GroupBy") || "Agrupar por"}>
                    <Button color={groupBy === "day" ? "primary" : "light"}
                      onClick={() => setGroupBy("day")}>{t("Day") || "Día"}</Button>
                    <Button color={groupBy === "week" ? "primary" : "light"}
                      onClick={() => setGroupBy("week")}>{t("Week") || "Semana"}</Button>
                    <Button color={groupBy === "month" ? "primary" : "light"}
                      onClick={() => setGroupBy("month")}>{t("Month") || "Mes"}</Button>
                  </div>
                </div>
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>{t("User")}</th>
                      <th className="text-end">{t("ValidatedServices")}</th>
                      <th className="text-end">{t("AvgValidationTime") || "Tiempo prom. validación"}</th>
                      <th>{t("Period") || "Período"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUser.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-muted">{t("NoResults")}</td></tr>
                    ) : (
                      byUser.map((row, i) => (
                        <tr key={`${row.user}-${row.period || row.month}-${i}`}>
                          <td>{subToName.get(row.user) || row.user}</td>
                          <td className="text-end">{row.validated_count}</td>
                          <td className="text-end">{fmtDuration(row.avg_validation_seconds)}</td>
                          <td>{row.period || row.month}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </div>
        ))}

        {/* ============================================================
            KPIs COMPARATIVOS (período actual vs anterior) — endpoint
            /dashboard/kpis. Sub-vista independiente (ruta .../dashboard/kpis).
            ============================================================ */}
        {showKpis && (
            <Card className="mt-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <CardTitle tag="h5" className="mb-0">
                    <i className="bx bx-trending-up me-1" />{t("KpisComparative")}
                  </CardTitle>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {/* Rango personalizado del bloque KPIs. */}
                    <div style={{ minWidth: 260 }}>
                      <DateRangeFilter t={t} value={kpiRange} onChange={setKpiRange} />
                    </div>
                    {/* Granularidad: Día / Semana / Mes. */}
                    <div className="btn-group btn-group-sm" role="group" aria-label={t("Granularity")}>
                      <Button color={kpiGranularity === "day" ? "primary" : "light"}
                        onClick={() => setKpiGranularity("day")}>{t("Day")}</Button>
                      <Button color={kpiGranularity === "week" ? "primary" : "light"}
                        onClick={() => setKpiGranularity("week")}>{t("Week")}</Button>
                      <Button color={kpiGranularity === "month" ? "primary" : "light"}
                        onClick={() => setKpiGranularity("month")}>{t("Month")}</Button>
                    </div>
                  </div>
                </div>

                {kpisError && <Alert color="danger">{kpisError}</Alert>}

                {kpisLoading ? (
                  <div className="text-center py-4"><Spinner color="primary" /></div>
                ) : !kpisData ? (
                  <div className="text-center text-muted py-4">{t("NoResults")}</div>
                ) : (
                  <>
                    {/* Etiquetas de período (actual vs anterior). */}
                    <div className="text-muted small mb-3">
                      <Badge color="primary" className="me-1">{t("CurrentPeriod")}</Badge>
                      {kpisData.current_period?.label}
                      <span className="mx-2">·</span>
                      <Badge color="light" className="me-1">{t("PreviousPeriod")}</Badge>
                      {kpisData.previous_period?.label}
                    </div>

                    {/* Secciones del comparativo en pestañas: Por usuario /
                        KPIs / Auditoría (en ese orden). */}
                    <Nav tabs className="mb-3">
                      <NavItem>
                        <NavLink
                          className={classnames({ active: kpiTab === "byuser" })}
                          onClick={() => setKpiTab("byuser")}
                          style={{ cursor: "pointer" }}>
                          <i className="bx bx-group me-1" />{t("ValidationsByUser")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: kpiTab === "kpis" })}
                          onClick={() => setKpiTab("kpis")}
                          style={{ cursor: "pointer" }}>
                          <i className="bx bx-trending-up me-1" />{t("KpisComparative")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: kpiTab === "audit" })}
                          onClick={() => setKpiTab("audit")}
                          style={{ cursor: "pointer" }}>
                          <i className="bx bx-list-ul me-1" />{t("AuditBreakdown")}
                        </NavLink>
                      </NavItem>
                    </Nav>

                    <TabContent activeTab={kpiTab}>
                      {/* Tab 1: Comparativo por usuario. Se RESPETA el orden del
                          backend (fila system:auto-validation SIEMPRE al final):
                          se itera by_user tal cual, sin reordenar. */}
                      <TabPane tabId="byuser">
                        <Table responsive hover className="mb-0 align-middle">
                          <thead>
                            <tr>
                              <th>{t("User")}</th>
                              {BY_USER_METRICS.map(({ key, labelKey }) => (
                                <th key={key} className="text-end">{t(labelKey)}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(kpisData.by_user || []).length === 0 ? (
                              <tr>
                                <td colSpan={BY_USER_METRICS.length + 1} className="text-center text-muted">
                                  {t("NoResults")}
                                </td>
                              </tr>
                            ) : (
                              kpisData.by_user.map((row) => (
                                <tr key={row.user}>
                                  <td>{row.name || row.user}</td>
                                  {BY_USER_METRICS.map(({ key, fmt }) => {
                                    const metric = row[key]
                                    const valueFmt = valueFormatter(fmt)
                                    return (
                                      <td key={key} className="text-end">
                                        <div>{valueFmt(metric?.current)}</div>
                                        <div className="small">
                                          <ComparisonCell metric={metric} fmt={fmt} t={t} />
                                        </div>
                                      </td>
                                    )
                                  })}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </TabPane>

                      {/* Tab 2: Tabla de KPIs: actual / anterior / variación. */}
                      <TabPane tabId="kpis">
                        <Table responsive hover className="mb-0 align-middle">
                          <thead>
                            <tr>
                              <th>{t("KpisComparative")}</th>
                              <th className="text-end">{t("CurrentPeriod")}</th>
                              <th className="text-end">{t("PreviousPeriod")}</th>
                              <th className="text-end">{t("Delta")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {KPI_ROWS.map(({ key, labelKey, fmt }) => {
                              const metric = kpisData.kpis?.[key]
                              const valueFmt = valueFormatter(fmt)
                              return (
                                <tr key={key}>
                                  <td>{t(labelKey)}</td>
                                  <td className="text-end">{valueFmt(metric?.current)}</td>
                                  <td className="text-end">{valueFmt(metric?.previous)}</td>
                                  <td className="text-end"><ComparisonCell metric={metric} fmt={fmt} t={t} /></td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </TabPane>

                      {/* Tab 3: Desglose de auditoría por verbo. */}
                      <TabPane tabId="audit">
                        <Table responsive hover className="mb-0 align-middle">
                          <thead>
                            <tr>
                              <th>{t("AuditActions")}</th>
                              <th className="text-end">{t("CurrentPeriod")}</th>
                              <th className="text-end">{t("PreviousPeriod")}</th>
                              <th className="text-end">{t("Delta")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {AUDIT_ROWS.map(({ key, labelKey }) => {
                              const metric = kpisData.audit_breakdown?.[key]
                              return (
                                <tr key={key}>
                                  <td>{t(labelKey)}</td>
                                  <td className="text-end">{formatNumber(metric?.current)}</td>
                                  <td className="text-end">{formatNumber(metric?.previous)}</td>
                                  <td className="text-end"><ComparisonCell metric={metric} fmt="number" t={t} /></td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </TabPane>
                    </TabContent>
                  </>
                )}
              </CardBody>
            </Card>
        )}

        {/* Tutorial del dashboard (modal de bienvenida + tour guiado). Solo en
            la vista General, que es la que tiene el tour y el botón que lo abre. */}
        {showGeneral && (
          <ServiceValidationTour
            t={t} variant="dashboard"
            open={tourOpen} onClose={() => setTourOpen(false)} />
        )}
      </Container>
    </div>
  )
}

export default withTranslation()(ServiceValidationDashboard)
